import React, { Component } from 'react';
import { uniq } from 'lodash';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import Badge from 'components/v2/Badge';
import { titleize, humanize, unescapeHTML } from 'underscore.string';
import { massUpdateActions } from 'constants/MassUpdates';
import { getUpdateFieldForAction } from 'helpers/MassUpdates';
import CargomaticInput from 'components/v2/input';
import { withTranslation } from 'react-i18next';
import { t } from 'i18next';
import { sluggify } from 'utils/localization/utils';
import ErrorAlert from '../../ui/ErrorAlert';
import TagEditor from 'components/ui/TagEditor';
import AdminConstants from 'constants/AdminConstants';
import { TERMINAL_HOLD_STATUS_VALUES } from 'constants/terminalHoldStatus';
import TextField from 'components/v2/forms/TextField';
import CargomaticSelect from 'components/v2/select';
import ReferenceNumberTypes from 'constants/ReferenceNumberTypes';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import { getClassName } from 'utils/ui/classes';
import { Button, DropdownMenu, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuContent, } from '@cargomatic/ui';
import { PlusCircleIcon } from 'lucide-react';
const SHORTHAUL_REF_NUMBERS = ReferenceNumberTypes.filter((r) => r.editable);
const EXCLUDED_REF_FIELDS = ['isa', 'truck_order', 'VRID', 'delivery_order'];
const defaultColumnHeaders = [
    { name: 'Shipment', localizationSlug: 'SHIPMENT', count: 1 },
    { name: 'Shipper', localizationSlug: 'SHIPPER' },
    { name: 'Equipment', localizationSlug: 'EQUIPMENT' },
];
const bulkAddReferenceHeaders = [
    { name: 'Shipment', localizationSlug: 'SHIPMENT' },
    { name: 'Shipper', localizationSlug: 'SHIPPER' },
    { name: 'Bulk Add Tags', localizationSlug: 'BULK_ADD_REFERENCE_VALUES' },
    { name: 'Apply To All', localizationSlug: 'APPLY_TO_ALL' },
    { name: 'Remove', localizationSlug: 'REMOVE' },
];
const bulkClearTagsHeaders = [
    { name: 'Shipment', localizationSlug: 'SHIPMENT' },
    { name: 'Shipper', localizationSlug: 'SHIPPER' },
    { name: 'Tags', localizationSlug: 'TAGS' },
    { name: 'Remove', localizationSlug: 'REMOVE' },
];
const bulkAddTagsHeaders = [
    { name: 'Shipment', localizationSlug: 'SHIPMENT' },
    { name: 'Shipper', localizationSlug: 'SHIPPER' },
    { name: 'Tags', localizationSlug: 'TAGS' },
    { name: 'Bulk Add Tags', localizationSlug: 'BULK_ADD_TAGS' },
    { name: 'Apply To All', localizationSlug: 'APPLY_TO_ALL' },
    { name: 'Remove', localizationSlug: 'REMOVE' },
];
const bulkAddTerminalHoldsHeaders = [
    { name: 'Shipment', localizationSlug: 'SHIPMENT' },
    { name: 'Shipper', localizationSlug: 'SHIPPER' },
    { name: 'Terminal Holds', localizationSlug: 'TERMINAL_HOLDS' },
    {
        name: 'Add Terminal Holds',
        localizationSlug: 'ADD_TERMINAL_HOLDS',
    },
    { name: 'Apply To All', localizationSlug: 'APPLY_TO_ALL' },
    { name: 'Remove', localizationSlug: 'REMOVE' },
];
const disableApplyToAllForLast = [
    massUpdateActions.BULK_ADD_TAGS,
    massUpdateActions.BULK_ADD_TERMINAL_HOLDS,
    massUpdateActions.BULK_ADD_REFERENCE_NUMBERS,
];
const disableApplyToAllIfEmpty = [massUpdateActions.BULK_ADD_REFERENCE_NUMBERS];
const hideClearAllActions = [massUpdateActions.BULK_CLEAR_TAGS];
class ShipmentsGroup extends Component {
    constructor(props) {
        super(props);
        this.handleRemoveShipment = (shipment) => () => this.props.onRemoveShipment(shipment);
        this.handleClearAll = () => this.props.onClearAll();
        this.handleApplyToAll = (shipmentId) => () => this.props.onApplyToAll(shipmentId);
        this.handleSaveAll = () => this.props.onSaveAll();
        this.handleDateFieldChange = (jobId, field) => (momentDateObj) => {
            const dateString = momentDateObj ? momentDateObj.toISOString() : null;
            this.props.onFieldChange(jobId, field, dateString);
        };
        this.handleTextChange = (jobId) => (inputString) => {
            this.setState({ notes: { [jobId]: inputString } });
        };
        this.handleCustomReferenceNumberChange = (value) => {
            this.props.handleReferenceNumberTypeChange('custom', value);
        };
        this.state = {
            notes: {},
        };
    }
    getActionName() {
        const { updateAction, t } = this.props;
        switch (updateAction) {
            case massUpdateActions.SHIPMENT_ARRIVED:
                return t('ARRIVE_ALL');
            case massUpdateActions.BULK_CLEAR_TAGS:
                return t('CLEAR_TAGS');
            default:
                return t('SAVE_ALL');
        }
    }
    getLocalisedHeaders(headers = []) {
        return headers === null || headers === void 0 ? void 0 : headers.map((header) => header.count
            ? t(header.localizationSlug, { count: header.count })
            : t(header.localizationSlug));
    }
    getHeaders() {
        const { updateAction, t } = this.props;
        const localizationSlug = Object.keys(massUpdateActions).find((key) => massUpdateActions[key] === updateAction) || '';
        const defaultColumnHeadersLocalization = defaultColumnHeaders.map((header) => header.count
            ? t(header.localizationSlug, { count: header.count })
            : t(header.localizationSlug));
        const bulkClearTagsHeadersLocalization = this.getLocalisedHeaders(bulkClearTagsHeaders);
        const bulkAddTagsHeadersLocalization = this.getLocalisedHeaders(bulkAddTagsHeaders);
        const bulkAddTerminalHoldsHeadersLocalization = this.getLocalisedHeaders(bulkAddTerminalHoldsHeaders);
        const bulkAddReferenceLocalization = this.getLocalisedHeaders(bulkAddReferenceHeaders);
        switch (updateAction) {
            case massUpdateActions.SHIPMENT_ARRIVED:
            case massUpdateActions.EDIT_ESTIMATED_ARRIVAL:
            case massUpdateActions.EDIT_ARRIVAL:
            case massUpdateActions.EDIT_DISCHARGE:
            case massUpdateActions.EDIT_LAST_FREE_DAY:
            case massUpdateActions.EDIT_LFD_AT_RAIL_FACILITY:
            case massUpdateActions.EDIT_ETA_TO_FINAL_RAIL_FACILITY:
            case massUpdateActions.BULK_APPLY_ADMIN_NOTES:
            case massUpdateActions.BULK_APPLY_SPECIAL_INSTRUCTIONS:
            case massUpdateActions.UPDATE_LAST_CUT:
            case massUpdateActions.UPDATE_EARLIEST_RETURN:
            case massUpdateActions.UPDATE_EARLIEST_GET_EMPTY:
            case massUpdateActions.UPDATE_EMPTY_READY_FOR_PICKUP:
            case massUpdateActions.UPDATE_EMPTY_RETURN:
            case massUpdateActions.UPDATE_REVIEW_DATE:
            case massUpdateActions.UPDATE_ACCOUNTING_REVIEW_DATE:
                return [
                    ...defaultColumnHeadersLocalization,
                    titleize(t(localizationSlug)),
                    t('APPLY_TO_ALL'),
                    titleize(t('REMOVE')),
                ];
            case massUpdateActions.BULK_CLEAR_TAGS:
                return bulkClearTagsHeadersLocalization;
            case massUpdateActions.BULK_ADD_TAGS:
                return bulkAddTagsHeadersLocalization;
            case massUpdateActions.BULK_ADD_TERMINAL_HOLDS:
                return bulkAddTerminalHoldsHeadersLocalization;
            case massUpdateActions.BULK_ADD_REFERENCE_NUMBERS:
                return bulkAddReferenceLocalization;
            default:
                return [...defaultColumnHeadersLocalization];
        }
    }
    getValidationClass(shipment, field) {
        const { status } = shipment;
        if (status[field]) {
            return {
                error: true,
            };
        }
        return {
            error: false,
        };
    }
    showClearAll() {
        const { updateAction } = this.props;
        return !hideClearAllActions.includes(updateAction);
    }
    renderColumns(shipment) {
        const { updateAction } = this.props;
        switch (updateAction) {
            case massUpdateActions.SHIPMENT_ARRIVED:
            case massUpdateActions.EDIT_ESTIMATED_ARRIVAL:
            case massUpdateActions.EDIT_ARRIVAL:
            case massUpdateActions.EDIT_DISCHARGE:
            case massUpdateActions.EDIT_LAST_FREE_DAY:
            case massUpdateActions.EDIT_ETA_TO_FINAL_RAIL_FACILITY:
            case massUpdateActions.EDIT_LFD_AT_RAIL_FACILITY:
            case massUpdateActions.UPDATE_LAST_CUT:
            case massUpdateActions.UPDATE_EARLIEST_RETURN:
            case massUpdateActions.UPDATE_REVIEW_DATE:
            case massUpdateActions.UPDATE_EMPTY_RETURN:
            case massUpdateActions.UPDATE_ACCOUNTING_REVIEW_DATE:
            case massUpdateActions.UPDATE_EARLIEST_GET_EMPTY: {
                const field = getUpdateFieldForAction(updateAction);
                return this.renderDateShipmentColumns(shipment, field);
            }
            case massUpdateActions.UPDATE_EMPTY_READY_FOR_PICKUP: {
                const field = getUpdateFieldForAction(updateAction);
                return this.renderDateShipmentColumns(shipment, field);
            }
            case massUpdateActions.BULK_APPLY_ADMIN_NOTES:
            case massUpdateActions.BULK_APPLY_SPECIAL_INSTRUCTIONS:
                {
                    const field = getUpdateFieldForAction(updateAction);
                    return this.renderTextInputColumns(shipment, field);
                }
            case massUpdateActions.BULK_CLEAR_TAGS: {
                return this.renderClearTagsColumns(shipment);
            }
            case massUpdateActions.BULK_ADD_TAGS: {
                return this.renderAddTagsColumns(shipment);
            }
            case massUpdateActions.BULK_ADD_TERMINAL_HOLDS: {
                return this.renderBulkAddTerminalHoldsColumns(shipment);
            }
            case massUpdateActions.BULK_ADD_REFERENCE_NUMBERS: {
                return this.renderAddReferenceColumns(shipment);
            }
            default:
                return renderDefaultColumns(shipment);
        }
    }
    renderDateShipmentColumns(shipment, field) {
        return [
            ...renderDefaultColumns(shipment),
            this.renderDateInput(shipment, field),
            this.renderApplyToAllButton(shipment),
            this.renderRemoveShipmentButton(shipment),
        ];
    }
    renderTextInputColumns(shipment, field) {
        return [
            ...renderDefaultColumns(shipment),
            this.renderTextInput(shipment, field),
            this.renderApplyToAllButton(shipment),
            this.renderRemoveShipmentButton(shipment),
        ];
    }
    renderAddBulkTags(shipment, field) {
        var _a;
        const { _id: jobId } = shipment;
        const { shipmentUpdates, shipmentUpdateResults } = this.props;
        const newTagsValue = ((_a = shipmentUpdates === null || shipmentUpdates === void 0 ? void 0 : shipmentUpdates[jobId]) === null || _a === void 0 ? void 0 : _a[field]) || [];
        const emptyFields = (shipmentUpdateResults === null || shipmentUpdateResults === void 0 ? void 0 : shipmentUpdateResults.flatMap((result) => result.job_ids)) || [];
        const isEmpty = emptyFields.includes(jobId);
        return (React.createElement("td", { key: `td-tag-editor-${jobId}` },
            React.createElement(TagEditor, { key: `Tag-Editor-${jobId}`, tags: newTagsValue, handleChange: (shipment, tags) => {
                    const { _id: jobId } = shipment;
                    this.props.onFieldChange(jobId, field, tags);
                }, object: shipment, suggestions: AdminConstants.shipmentTagSuggestions, wrapperClasses: isEmpty ? 'border-2 border-red-500 rounded-md' : '' })));
    }
    renderAddTerminalHoldsStatus(shipment, field) {
        var _a;
        const { _id: jobId } = shipment;
        const { t, shipmentUpdates, onFieldChange } = this.props;
        const terminalHoldsValue = ((_a = shipmentUpdates === null || shipmentUpdates === void 0 ? void 0 : shipmentUpdates[jobId]) === null || _a === void 0 ? void 0 : _a[field]) || [];
        const handleSelectTerminalHoldStatus = (terminalHold) => {
            return (checked) => {
                if (checked) {
                    const newValues = uniq(terminalHoldsValue.concat(terminalHold));
                    onFieldChange(jobId, field, newValues);
                }
                else {
                    const newValues = terminalHoldsValue.filter((current) => current !== terminalHold);
                    onFieldChange(jobId, field, newValues);
                }
            };
        };
        return (React.createElement("td", { key: `td-bulk-terminal-hold-editor-${jobId}` },
            React.createElement(DropdownMenu, { className: "mb-4" },
                React.createElement(DropdownMenuTrigger, { asChild: true },
                    React.createElement(Button, { variant: "outline", size: "sm", className: "h-10 gap-1" },
                        React.createElement("span", { className: "sr-only sm:not-sr-only sm:whitespace-nowrap" }, t('TERMINAL_HOLDS_STATUS')),
                        React.createElement(PlusCircleIcon, { className: "h-3.5 w-3.5" }))),
                React.createElement(DropdownMenuContent, { align: "end", className: "z-[9999] max-h-72 overflow-y-auto" },
                    React.createElement(DropdownMenuLabel, null, t('TERMINAL_HOLDS_STATUS')),
                    React.createElement(DropdownMenuSeparator, null),
                    TERMINAL_HOLD_STATUS_VALUES.map((terminalHold) => (React.createElement(DropdownMenuCheckboxItem, { key: terminalHold, onCheckedChange: handleSelectTerminalHoldStatus(terminalHold), checked: terminalHoldsValue.includes(terminalHold) },
                        React.createElement("span", { "data-testid": `dropdown-option-${terminalHold}` }, t(sluggify(terminalHold))))))))));
    }
    renderReferenceNumberDropdown(shipment) {
        const { t, shipperReferenceNumbers, referenceNumberType } = this.props;
        const isIntermodalShipment = isIntermodalShipmentType(shipment === null || shipment === void 0 ? void 0 : shipment.shipment_type);
        let displayedFields = SHORTHAUL_REF_NUMBERS.filter((field) => !EXCLUDED_REF_FIELDS.includes(field.name));
        if (isIntermodalShipment) {
            displayedFields = shipperReferenceNumbers;
        }
        displayedFields = displayedFields
            .sort((a, b) => a.name.localeCompare(b.name))
            .concat([
            {
                name: 'custom',
                localizationSlug: titleize(t('CUSTOM')),
            },
        ]);
        const referenceNumberRequired = !referenceNumberType.custom && !referenceNumberType.name
            ? t('REFERENCE_NUMBER_COMPULSORY')
            : '';
        const customReferenceRequired = !referenceNumberType.customReferenceNumber
            ? t('CUSTOM_REFERENCE_NUMBER_REQUIRED')
            : '';
        const selectClass = getClassName('w-full mt-1', {
            'border-red-500': referenceNumberRequired,
            'text-red-500': referenceNumberRequired,
        });
        return (React.createElement("div", { className: "flex gap-2" },
            React.createElement("div", { className: "w-1/2" },
                React.createElement(CargomaticSelect, { showCaption: true, label: t('REFERENCE_NUMBER_TYPE'), selected: referenceNumberType.name, className: 'w-full', inputClassNames: selectClass, items: displayedFields, onSelect: (item) => {
                        this.props.handleReferenceNumberTypeChange(item.name, '');
                    }, applyDefaultMargins: false, valueKey: "name", textKey: "localizationSlug", error: { message: referenceNumberRequired }, dataTestId: 'select-reference-number-uv', caption: t('SELECT_A_TYPE') })),
            referenceNumberType.custom && (React.createElement("div", { className: "w-1/2" },
                React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-0406967e", error: { message: customReferenceRequired }, className: "mb-2", label: t('CUSTOM_REFERENCE_NUMBER_TYPE'), type: "text", placeholder: t('REF_#_NAME'), onChange: this.handleCustomReferenceNumberChange, value: unescapeHTML((referenceNumberType === null || referenceNumberType === void 0 ? void 0 : referenceNumberType.customReferenceNumber) || '') })))));
    }
    renderEditReferenceNumber(shipment, field) {
        var _a;
        const { _id: jobId } = shipment;
        const { shipmentUpdates, referenceNumberType } = this.props;
        const refNumber = ((_a = shipmentUpdates === null || shipmentUpdates === void 0 ? void 0 : shipmentUpdates[jobId]) === null || _a === void 0 ? void 0 : _a[field]) || [];
        const referenceNumberExists = referenceNumberType.custom
            ? referenceNumberType.customReferenceNumber
            : referenceNumberType.name;
        const disabled = !referenceNumberExists;
        return (React.createElement("td", { "data-testid": `td-add-ref-numbers-${jobId}` },
            React.createElement(TextField, { disabled: disabled, dataTestId: "TextField-31cf1f5f", type: "text", value: unescapeHTML(refNumber), placeholder: t('REF_#_VALUE'), onChange: (value) => {
                    this.props.onFieldChange(jobId, field, value);
                } })));
    }
    renderShipmentNumber(shipment) {
        const { _id: id } = shipment;
        const idKey = `id_${id}`;
        const shipmentLink = `/shipments/${id}`;
        return [
            React.createElement("td", { key: idKey },
                React.createElement("a", { href: shipmentLink, target: "_blank", rel: "noreferrer" }, shipment.shipment_id)),
        ];
    }
    renderShipperName(shipment) {
        const { _id: id } = shipment;
        const shipperNameKey = `shipper_${id}`;
        return [React.createElement("td", { key: shipperNameKey }, shipment.shipper_name)];
    }
    renderShipmentTags(shipment, field) {
        var _a;
        const { _id: id } = shipment;
        const tags = ((_a = shipment === null || shipment === void 0 ? void 0 : shipment[field]) === null || _a === void 0 ? void 0 : _a.filter((tag) => tag)) || [];
        return (React.createElement("td", null,
            React.createElement("div", { className: "flex flex-wrap gap-2" }, tags.map((tag, tag_id) => (React.createElement("div", { key: `${id}_${tag_id}`, className: "px-2 py-1 bg-gray-200 rounded text-sm whitespace-nowrap" }, tag))))));
    }
    renderShipmentTerminalHoldsStatus(shipment, field) {
        var _a;
        const { _id: jobId } = shipment;
        const { t, shipmentUpdates, onFieldChange } = this.props;
        const terminalHolds = ((_a = shipmentUpdates === null || shipmentUpdates === void 0 ? void 0 : shipmentUpdates[jobId]) === null || _a === void 0 ? void 0 : _a[field]) || [];
        const handleRemoveTerminalHoldStatus = (terminalHold) => {
            const newValues = terminalHolds.filter((current) => current !== terminalHold);
            onFieldChange(jobId, field, newValues);
        };
        return (React.createElement("td", null,
            React.createElement("div", { className: "flex flex-wrap gap-2" }, terminalHolds.map((terminalHold) => (React.createElement(Badge, { size: "large", rounded: true, withRemove: true, onRemove: () => handleRemoveTerminalHoldStatus(terminalHold) },
                React.createElement("span", { "data-testid": `badge-span-${terminalHold}` }, titleize(t(sluggify(terminalHold))))))))));
    }
    renderClearTagsColumns(shipment) {
        return [
            this.renderShipmentNumber(shipment),
            this.renderShipperName(shipment),
            this.renderShipmentTags(shipment, 'tags'),
            this.renderRemoveShipmentButton(shipment),
        ];
    }
    renderAddTagsColumns(shipment) {
        return [
            this.renderShipmentNumber(shipment),
            this.renderShipperName(shipment),
            this.renderShipmentTags(shipment, 'tags'),
            this.renderAddBulkTags(shipment, 'newTags'),
            this.renderApplyToAllButton(shipment, 'newTags'),
            this.renderRemoveShipmentButton(shipment),
        ];
    }
    renderBulkAddTerminalHoldsColumns(shipment) {
        return [
            this.renderShipmentNumber(shipment),
            this.renderShipperName(shipment),
            this.renderShipmentTerminalHoldsStatus(shipment, 'terminal_hold_status'),
            this.renderAddTerminalHoldsStatus(shipment, 'terminal_hold_status'),
            this.renderApplyToAllButton(shipment, 'terminal_hold_status'),
            this.renderRemoveShipmentButton(shipment),
        ];
    }
    renderAddReferenceColumns(shipment) {
        return [
            this.renderShipmentNumber(shipment),
            this.renderShipperName(shipment),
            this.renderEditReferenceNumber(shipment, 'new_ref_val'),
            this.renderApplyToAllButton(shipment, 'new_ref_val'),
            this.renderRemoveShipmentButton(shipment),
        ];
    }
    renderTextInput(shipment, field) {
        const { shipmentUpdates } = this.props;
        const selectedNote = shipmentUpdates[shipment._id] && shipmentUpdates[shipment._id][field];
        const noteKey = `note_${shipment._id}_${selectedNote}`;
        const className = Object.assign({ 'md:w-1/6': true }, this.getValidationClass(shipment, 'text'));
        return (React.createElement("td", { key: noteKey, className: classnames(className) },
            React.createElement("div", null,
                React.createElement(CargomaticInput, { type: "textarea", key: noteKey, onBlur: () => this.props.onFieldChange(shipment._id, field, this.state.notes[shipment._id]), defaultValue: selectedNote, value: typeof this.state.notes[shipment._id] === 'string'
                        ? this.state.notes[shipment._id]
                        : selectedNote, placeholderText: field, onChange: this.handleTextChange(shipment._id) }))));
    }
    renderDateInput(shipment, field) {
        const { shipmentUpdates, t } = this.props;
        const selectedDate = shipmentUpdates[shipment._id] && shipmentUpdates[shipment._id][field];
        const datePickerKey = `datePicker_${shipment._id}_${selectedDate}`;
        const className = Object.assign({}, this.getValidationClass(shipment, 'date'));
        return (React.createElement("td", { key: datePickerKey, className: classnames(className) },
            React.createElement("div", null,
                React.createElement(DatePicker, { key: datePickerKey, selected: mapDateValue(selectedDate), placeholderText: t(sluggify(humanize(field))), className: "w-full shadow border rounded px-3 -ml-3", showTimeSelect: true, dateFormat: "MM/DD/YYYY hh:mm a", timeIntervals: 30, onChange: this.handleDateFieldChange(shipment._id, field) }))));
    }
    getDisableApplyToAll(shipment, field) {
        var _a, _b;
        let disableApplyToAll = false;
        const { _id: jobId } = shipment;
        const { shipmentUpdates, action, shipments } = this.props;
        const lastShipment = shipments[shipments.length - 1];
        const shipmentUpdate = shipmentUpdates === null || shipmentUpdates === void 0 ? void 0 : shipmentUpdates[jobId];
        if (action === massUpdateActions.BULK_ADD_TAGS) {
            disableApplyToAll = !((_a = shipmentUpdate === null || shipmentUpdate === void 0 ? void 0 : shipmentUpdate[field]) === null || _a === void 0 ? void 0 : _a.length);
        }
        if (action === massUpdateActions.BULK_ADD_TERMINAL_HOLDS) {
            disableApplyToAll = !((_b = shipmentUpdate === null || shipmentUpdate === void 0 ? void 0 : shipmentUpdate[field]) === null || _b === void 0 ? void 0 : _b.length);
        }
        if (disableApplyToAllIfEmpty.includes(action)) {
            disableApplyToAll = !(shipmentUpdate === null || shipmentUpdate === void 0 ? void 0 : shipmentUpdate[field]);
        }
        if (disableApplyToAllForLast.includes(action) &&
            lastShipment._id === jobId) {
            disableApplyToAll = true;
        }
        return disableApplyToAll;
    }
    renderApplyToAllButton(shipment, field) {
        const applyAllKey = `apply_${shipment._id}`;
        const { t } = this.props;
        const buttonClassName = {
            button: true,
            'button-hollow-green': true,
        };
        const disableApplyToAll = this.getDisableApplyToAll(shipment, field);
        return (React.createElement("td", { key: applyAllKey },
            React.createElement("button", { type: "button", "data-testid": `${applyAllKey}-button`, className: classnames(buttonClassName), onClick: this.handleApplyToAll(shipment._id, field), disabled: disableApplyToAll }, titleize(t('APPLY')))));
    }
    renderRemoveShipmentButton(shipment) {
        const removeShipmentKey = `remove_${shipment._id}`;
        return (React.createElement("td", { key: removeShipmentKey },
            React.createElement("button", { className: "button button-secondary", onClick: this.handleRemoveShipment(shipment) },
                React.createElement("i", { className: "fa fa-remove" }))));
    }
    renderShipment(shipment) {
        const columns = this.renderColumns(shipment);
        return (React.createElement("tr", { key: shipment._id, className: "p-3" }, columns));
    }
    renderHeader() {
        const columnHeaders = this.getHeaders();
        return (React.createElement("thead", null,
            React.createElement("tr", null, columnHeaders.map((col) => (React.createElement("th", { key: col, "data-testid": `header-${col}`, className: "font-bold text-grey-darker" }, col))))));
    }
    renderBody() {
        const { shipments } = this.props;
        return (React.createElement("tbody", null, shipments.map((shipment) => this.renderShipment(shipment))));
    }
    renderStatuses() {
        const { shipmentUpdateResults } = this.props;
        if (!shipmentUpdateResults || shipmentUpdateResults.length === 0) {
            return null;
        }
        return (React.createElement("div", { className: "shipment-status-wrapper flex flex-col mr-4" }, shipmentUpdateResults.map((s) => renderStatus(s))));
    }
    render() {
        const { title, saving, disableSave, t, disableClearAll, shipments, action, } = this.props;
        const buttonDisabled = saving || disableSave;
        return (React.createElement("div", { className: "shadow bg-white p-4 mb-6" },
            React.createElement("div", { className: "flex justify-between", "data-testid": "mass-update-shipment-modal-title" },
                React.createElement("h4", { className: "mb-2 text-grey-darker" }, title)),
            React.createElement("hr", null),
            React.createElement("div", null, action === massUpdateActions.BULK_ADD_REFERENCE_NUMBERS &&
                this.renderReferenceNumberDropdown((shipments === null || shipments === void 0 ? void 0 : shipments[0]) || {})),
            React.createElement("hr", null),
            React.createElement("div", { className: "shipment-rows" },
                React.createElement("table", { className: "table" },
                    this.renderHeader(),
                    this.renderBody()),
                React.createElement("div", { className: "flex justify-end items-baseline" },
                    this.renderStatuses(),
                    this.showClearAll() && (React.createElement("button", { className: "button button-hollow-green mr-2", onClick: this.handleClearAll, disabled: disableClearAll }, t('CLEAR_ALL'))),
                    React.createElement("button", { className: `button button-green flex items-center unifiedView-masterList-${this.props.updateAction} heap`, disabled: buttonDisabled, onClick: this.handleSaveAll, "data-testid": "mass-update-modal-action-button" },
                        saving && (React.createElement("div", { className: "flex items-center mr-2" },
                            React.createElement("i", { className: "fa fa-spinner fa-spin" }))),
                        React.createElement("span", null, this.getActionName()))))));
    }
}
ShipmentsGroup.propTypes = {
    updateAction: PropTypes.string.isRequired,
    shipmentUpdates: PropTypes.object,
    shipments: PropTypes.array.isRequired,
    shipmentUpdateResults: PropTypes.array,
    title: PropTypes.string,
    saving: PropTypes.bool,
    disableSave: PropTypes.bool,
    onClearAll: PropTypes.func.isRequired,
    onSaveAll: PropTypes.func.isRequired,
    onApplyToAll: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
    shipperReferenceNumbers: PropTypes.array,
    handleReferenceNumberTypeChange: PropTypes.func.isRequired,
    referenceNumberType: PropTypes.object,
};
function renderStatus(status) {
    switch (status.type) {
        case 'error':
            return (React.createElement(ErrorAlert, { key: status.type, title: status.message }, status.items.length && (React.createElement("ul", null, status.items.map((id) => (React.createElement("li", { key: id }, id)))))));
        case 'success':
            return (React.createElement("div", { className: "border-solid border-l-4 border-green bg-green-lightest text-green-darker mb-4 p-4" },
                React.createElement("p", { className: "font-bold mb-2" }, status.message),
                status.items.length && (React.createElement("ul", null, status.items.map((id) => (React.createElement("li", { key: id }, id)))))));
        default:
            return null;
    }
}
function formatEquipments(shipment) {
    const equipments = shipment.stops[0].equipments || [];
    if (equipments.length === 0) {
        return t('NONE');
    }
    return (React.createElement("ul", null, equipments.map((e) => (React.createElement("li", { className: "mb-2", key: e._id }, e.equipment_id)))));
}
function mapDateValue(value) {
    if (!value) {
        return null;
    }
    if (value instanceof moment) {
        return value;
    }
    return moment(value);
}
function renderDefaultColumns(shipment) {
    const { _id: id } = shipment;
    const idKey = `id_${id}`;
    const shipperNameKey = `shipper_${id}`;
    const equipmentsKey = `equipments_${id}`;
    const shipmentLink = `/shipments/${id}`;
    return [
        React.createElement("td", { key: idKey },
            React.createElement("a", { href: shipmentLink, target: "_blank", rel: "noreferrer" }, shipment.shipment_id)),
        React.createElement("td", { key: shipperNameKey }, shipment.shipper_name),
        React.createElement("td", { key: equipmentsKey }, formatEquipments(shipment)),
    ];
}
export default withTranslation()(ShipmentsGroup);
